import { useEffect, useState } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Typography, Box, CircularProgress } from '@mui/material';

// api
import BuildingApi from '../../api/building.api';
import TermApi from '../../api/term.api';
import { useAppSelector } from '../../hooks/hooks';

// comps
import GlobalButton from '../../components/global/button';
import GlobalCheckbox from '../../components/global/checkbox';
import GlobalDropdown from '../../components/global/dropdown';
import Label from '../../components/global/label';
import FilterOption from '../../components/global/filterOption';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

import { colors } from '../../theme';

// constants
import { courseTimeArr } from '../../constants';
import { MOMENT_DATE_FORMAT } from '../../constants/date_format';
import { downloadExcelFile } from '../../helpers/file';

const WeeklyReport = (props: any) => {
    const _buildingApi = new BuildingApi();
    const _termApi = new TermApi();

    const appState = useAppSelector((state) => state.appState);

    const [state, setState] = useState({
        termData: [],
        mondays: [],
    });

    const [buildings, setBuildings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [selected, setSelected] = useState({
        // Weekly Report props
        term: null,
        building: null,
        isNight: false,
        week: null,
        includeSet: true, // default to TRUE if omitted
    });

    const disabledState = !selected.week;

    useEffect(() => {
        const loadData = async (campusId: number) => {
            const termData = await _termApi.GetTermsByCampusId(campusId);
            const buildings = await _buildingApi.GetAllBuildings(campusId);
            let selectedBuilding = null;

            // //Auto select if there is only one building. The building dropdown will be hidden
            if (buildings.length === 0) {
                selectedBuilding = buildings[-1];
            }

            setState({
                ...state,
                termData,
                mondays: getMondaysOfTerm(appState?.term),
            });

            setBuildings(buildings);

            setSelected({
                ...selected,
                building: selectedBuilding,
            });
        };

        if (appState.campus?.id) {
            loadData(appState.campus.id).catch((error) => toast.error('Error loading term data'));
        }
    }, [appState.campus]);

    const getMondaysOfTerm = (value: any) => {
        let startDate = moment(value.startDate);
        let endDate = moment(value.endDate);

        var mondays = [];
        // Get "next" monday
        let tmp = startDate.clone().day(1);
        if (tmp.isAfter(startDate, 'd')) {
            mondays.push(tmp.format(MOMENT_DATE_FORMAT));
        }
        while (tmp.isBefore(endDate)) {
            tmp.add(7, 'days');
            mondays.push(tmp.format(MOMENT_DATE_FORMAT));
        }

        mondays = mondays.map((str, index) => ({ date: str, dateId: index + 1 }));
        return mondays;
    };

    const handleSelection = (name: any, value: any, isTermChange: boolean) => {
        if (isTermChange && value) {
            const mondays = getMondaysOfTerm(value);

            setState((prevState) => {
                return {
                    ...prevState,
                    mondays,
                };
            });
        }

        setSelected((prevState) => {
            return {
                ...prevState,
                [name]: value,
            };
        });
    };

    const handleClearWeekly = () => {
        setSelected({
            ...selected,
            term: null,
            building: null,
            week: null,
            includeSet: true,
        });
        setState({
            ...state,
            mondays: [],
        });
    };

    const handleGenerateWeeklyReport = async () => {
        try {
            setIsLoading(true);
            const reportData = await _buildingApi.GetWeeklyReport(selected.building?.id, selected.week?.date, selected?.includeSet, selected.isNight);
            downloadExcelFile(reportData.fileBlob, reportData.fileName);
        } catch (error) {
            toast.error('Error generating weekly report.');
        } finally {
            handleClearWeekly();
            setIsLoading(false);
        }
    };

    const handleSelectTimeFrame = (value: any) => {
        if (disabledState) return;
        let isNight = false;
        if (value === 'night') isNight = true;
        setSelected({
            ...selected,
            isNight,
        });
    };

    const includeSetArr = [
        {
            label: 'Yes',
            value: true,
            checked: selected.includeSet === true,
        },
        {
            label: 'No',
            value: false,
            checked: selected.includeSet === false,
        },
    ];

    const handleSelectIncludeSet = (name: any, value: any) => {
        setSelected({
            ...selected,
            [name]: value === 'Yes',
        });
    };

    return (
        <>
            {isLoading && (
                <Loader>
                    <CircularProgress size={49} />
                </Loader>
            )}
            <Box sx={{ height: '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReportBox sx={{ mr: '2em' }}>
                    <FlexBox sx={{ mb: '20px' }}>
                        <Typography variant="h4" color={colors.blue.main} sx={{ mr: '10px' }}>
                            Weekly Report
                        </Typography>
                        <Typography variant="body1" color={colors.grey.medium}>
                            Export weekly schedule
                        </Typography>
                    </FlexBox>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            '& > *': {
                                mb: '10px',
                            },
                        }}
                    >
                        <GlobalDropdown
                            label="term"
                            placeholder="Select a term"
                            data={state.termData}
                            value={selected.term}
                            dataItemKey={'term'}
                            name={'termCode'}
                            field={'termId'}
                            onChange={(name: any, value: any) => handleSelection(name, value, true)}
                            width={'100px'}
                        />
                        <GlobalDropdown
                            label="building"
                            placeholder="Select a building"
                            data={buildings}
                            value={selected.building}
                            name="name"
                            field="id"
                            dataItemKey={'building'}
                            onChange={(name: any, value: any) => handleSelection(name, value, false)}
                            width={'100px'}
                            isSearch
                        />
                        <GlobalDropdown
                            label="date"
                            placeholder="Select Monday of the Week"
                            isSearch
                            data={state.mondays}
                            value={selected.week}
                            dataItemKey={'week'}
                            name={'date'}
                            field={'dateId'}
                            disabled={!selected.term}
                            onChange={(name: any, value: any) => handleSelection(name, value, false)}
                        />
                        <FilterOption
                            label="Time"
                            data={courseTimeArr}
                            disabled={disabledState}
                            name={'label'}
                            state={selected.isNight ? 'night' : 'day'}
                            onClickValue={'value'}
                            onOptionClick={handleSelectTimeFrame}
                        />
                        <FlexBox>
                            <Label disabled={disabledState} label="include set" />
                            {includeSetArr.map((item: any, index: any) => {
                                return <GlobalCheckbox key={index} isChecked={item.checked} name="includeSet" label={item.label} onChange={handleSelectIncludeSet} disabled={disabledState} />;
                            })}
                        </FlexBox>
                        <FlexBox sx={{ justifyContent: 'space-between' }}>
                            <GlobalButton variant="outlined" text="reset" endIcon={<RefreshIcon />} onClick={handleClearWeekly} />
                            <GlobalButton variant="contained" text="generate report" disabled={!selected.week || selected.includeSet === null} onClick={handleGenerateWeeklyReport} />
                        </FlexBox>
                    </Box>
                </ReportBox>
            </Box>
        </>
    );
};

export default WeeklyReport;

const Loader = styled.div`
    position: fixed;
    left: 0;
    top: 0;
    z-index: 99999;
    width: 100vw;
    height: 100vh;
    background: rgba(255, 255, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ReportBox = styled(Box)`
    && {
        background-color: ${colors.white};
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
        border-radius: 5px;
        padding: 20px 15px;
        min-width: 345px;
        /* max-width: 350px; */
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;

        & > * {
            margin-right: 10px;
        }
    }
`;
